import React, { useContext, useMemo, useReducer } from "react";
import { type StorageService, useStorage } from "@/lib/storage";

export interface GlobalReducerStateComponentProps {
	children: React.ReactNode;
}

export type ReducerStateInitializerFunc<S> = (storage: StorageService) => S;

export const createGlobalReducerState = <S, A>(reducer: React.Reducer<S, A>, stateInitializer: ReducerStateInitializerFunc<S>): [(props: GlobalReducerStateComponentProps) => React.JSX.Element, () => [S, React.Dispatch<A>]] => {
	const ctx = React.createContext<[S, React.Dispatch<A>] | null>(null);

	const ReducerStateProviderComponent = ({ children }: GlobalReducerStateComponentProps): React.JSX.Element => {
		const storage = useStorage();
		const initialState = useMemo(() => stateInitializer(storage), [storage]);
		const [state, dispatch] = useReducer(reducer, initialState);
		return (
			<ctx.Provider value={[state, dispatch]}>
				{children}
			</ctx.Provider>
		);
	};

	const useReducerStateContext = () => useContext(ctx) as [S, React.Dispatch<A>];
	return [ReducerStateProviderComponent, useReducerStateContext];
};
